import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Icon from '../components/Icon';
import ImageLightbox from '../components/ImageLightbox';
import { DetailCard, DetailRow, ImageThumb, cap, fmtDate, fmtDateTime } from '../components/DetailParts';
import { useToast } from '../components/Toast';
import { useAuth } from '../context/AuthContext';
import {
  APPLICATION_STATUSES,
  ApplicationStatus,
  RentalApplication,
  extractApiError,
  fileSrc,
  formatMoney,
  getApplication,
  statusBadgeClass,
  statusLabel,
  updateApplicationStatus,
} from '../api/applications';
import { Listing, getListing } from '../api/listings';
import { isImageUrl } from '../api/uploadUrl';

type Viewing = { src: string; title: string } | null;

const dash = (v?: string | number | null): React.ReactNode =>
  v === null || v === undefined || v === '' ? <span className="text-g400 font-normal">—</span> : v;

const fileExt = (url: string): string => {
  const m = /\.([a-z0-9]{2,5})(\?|#|$)/i.exec(url);
  return m ? m[1].toUpperCase() : 'FILE';
};

/** One group of uploaded files: images as thumbnails, anything else (PDF) as a link. */
const FileGroup: React.FC<{
  label: string;
  files?: string[];
  onOpen: (src: string, title: string) => void;
}> = ({ label, files, onOpen }) => {
  const list = (Array.isArray(files) ? files : []).filter(Boolean);
  return (
    <div className="py-3 border-b border-g100 last:border-b-0">
      <div className="text-[13px] text-g500 mb-2">{label}</div>
      {list.length ? (
        <div className="flex flex-wrap gap-2.5">
          {list.map((f, i) => {
            const src = fileSrc(f);
            const title = list.length > 1 ? `${label} ${i + 1}` : label;
            return isImageUrl(f) ? (
              <ImageThumb key={i} src={src} alt={title} size={104} onOpen={() => onOpen(src, title)} />
            ) : (
              <a
                key={i}
                href={src}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 rounded-xl border border-g200 px-3.5 py-2.5 text-[13px] font-semibold text-ink hover:border-brand hover:text-brand transition-colors"
              >
                <Icon name="file-text" size={16} color="var(--brand)" />
                {title}
                <span className="text-[11px] font-bold text-g500 bg-g100 rounded px-1.5 py-0.5">{fileExt(f)}</span>
              </a>
            );
          })}
        </div>
      ) : (
        <span className="text-[13px] text-g400">Not provided</span>
      )}
    </div>
  );
};

/**
 * A rental application (Rental Application page → View): everything the
 * applicant submitted — personal details, employment, references, previous
 * landlord, uploaded documents and payment — plus the property applied for.
 */
const ApplicationDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuth();
  const [app, setApp] = useState<RentalApplication | null>(null);
  const [listing, setListing] = useState<Listing | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState<boolean>(false);
  const [viewing, setViewing] = useState<Viewing>(null);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const a = await getApplication(Number(id));
      setApp(a);
      // The full listing (photos, details) is a nice-to-have: the application
      // already carries a summary, so a failure here isn't fatal.
      getListing(a.listingId)
        .then(setListing)
        .catch(() => setListing(null));
    } catch (err) {
      setError(extractApiError(err, 'Failed to load application'));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const changeStatus = async (next: ApplicationStatus) => {
    if (!app || next === app.status) return;
    setSaving(true);
    try {
      const updated = await updateApplicationStatus(app.id, next);
      setApp((a) => (a ? { ...a, ...updated } : a));
      toast(`${app.referenceNo || 'Application'} → ${statusLabel(next)}`, 'success');
    } catch (err) {
      toast(extractApiError(err, 'Failed to update status'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const backToList = () => navigate('/admin/applications');
  const open = (src: string, title: string) => setViewing({ src, title });

  if (loading) {
    return (
      <div className="py-20 text-center text-g500">
        <Icon name="file-text" size={28} color="var(--g400)" /> Loading application...
      </div>
    );
  }

  if (error || !app) {
    return (
      <div className="card p-6 max-w-[520px]">
        <div className="flex items-center gap-2 mb-2" style={{ color: 'var(--red)' }}>
          <Icon name="x-circle" size={18} color="var(--red)" />
          <strong>Couldn't load this application</strong>
        </div>
        <div className="text-sm text-g600 mb-4">{error || 'Application not found.'}</div>
        <div className="flex gap-2">
          <button className="btn btn-secondary btn-sm" onClick={load}>
            Try again
          </button>
          <button className="btn btn-ghost btn-sm" onClick={backToList}>
            Back to rental applications
          </button>
        </div>
      </div>
    );
  }

  const references = Array.isArray(app.references) ? app.references : [];
  const paid = Boolean(app.feePaid || app.paidAt);

  // Property: prefer the full listing, fall back to the application's summary.
  const listingPhotos = (
    listing?.photos && listing.photos.length
      ? listing.photos
      : listing?.coverPhoto || app.listing?.coverPhoto
      ? [(listing?.coverPhoto || app.listing?.coverPhoto) as string]
      : []
  ).map(fileSrc);
  const listTitle = listing?.title || app.listing?.title || app.listing?.address || `Listing #${app.listingId}`;
  const listAddress = listing?.address || app.listing?.address;
  const listType = listing?.type || app.listing?.type;
  const listPrice = listing?.price ?? app.listing?.price ?? null;
  const beds = listing?.beds ?? app.listing?.beds ?? null;
  const baths = listing?.baths ?? app.listing?.baths ?? null;
  const agentIsMe = app.agent && user && String(app.agent.id) === String(user.id);

  return (
    <div className="animate-fadeIn">
      {/* Top bar */}
      <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
        <button
          className="flex items-center gap-2 text-sm font-semibold text-g600 hover:text-ink transition-colors"
          onClick={backToList}
        >
          <Icon name="arrow-left" size={18} /> Back to rental applications
        </button>
        <div className="flex items-center gap-2.5">
          <span className="text-[13px] text-g500">Update status</span>
          <select
            value={app.status}
            disabled={saving}
            onChange={(e) => changeStatus(e.target.value as ApplicationStatus)}
            className="form-input"
            style={{ width: 170, padding: '7px 10px', fontWeight: 600, cursor: saving ? 'wait' : 'pointer' }}
          >
            {APPLICATION_STATUSES.map((s) => (
              <option key={s} value={s}>
                {statusLabel(s)}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Heading */}
      <div className="card p-6 mb-5 flex items-center justify-between flex-wrap gap-4">
        <div>
          <div className="text-[12px] text-g500 font-semibold uppercase tracking-wider">Rental application</div>
          <div className="text-2xl font-extrabold text-ink">{app.referenceNo || `#${app.id}`}</div>
          <div className="text-[13px] text-g500 mt-0.5">
            Submitted {fmtDateTime(app.createdAt)} · Last updated {fmtDateTime(app.updatedAt)}
          </div>
        </div>
        <div className="flex items-center gap-6">
          <div className="text-right">
            <div className="text-[12px] text-g500">Application fee</div>
            <div className="text-2xl font-extrabold text-brand">{formatMoney(app.applicationFee)}</div>
          </div>
          <span className={statusBadgeClass(app.status)}>{statusLabel(app.status)}</span>
        </div>
      </div>

      <div className="grid gap-5 lg:grid-cols-2 items-start">
        {/* Left: what the applicant filled in */}
        <div className="flex flex-col gap-5">
          <DetailCard icon="user" title="Applicant">
            <DetailRow label="Full name">
              <button
                type="button"
                className="text-brand hover:underline font-semibold text-right"
                onClick={() => navigate(`/admin/users/${app.userId}`)}
              >
                {app.fullName}
              </button>
            </DetailRow>
            <DetailRow label="Email">
              {app.applicant?.email ? (
                <a href={`mailto:${app.applicant.email}`} className="hover:text-brand">
                  {app.applicant.email}
                </a>
              ) : (
                dash(null)
              )}
            </DetailRow>
            <DetailRow label="Phone">
              {app.phone ? (
                <a href={`tel:${app.phone}`} className="hover:text-brand">
                  {app.phone}
                </a>
              ) : (
                dash(null)
              )}
            </DetailRow>
            <DetailRow label="Date of birth">{fmtDate(app.dateOfBirth)}</DetailRow>
            <DetailRow label="Current address">{dash(app.currentAddress)}</DetailRow>
            <DetailRow label="Time at this address">{dash(app.durationAtAddress)}</DetailRow>
          </DetailCard>

          <DetailCard icon="dollar-sign" title="Employment & Income">
            <DetailRow label="Employment status">{cap(app.employmentStatus)}</DetailRow>
            <DetailRow label="Employer">{dash(app.employerName)}</DetailRow>
            <DetailRow label="Job title">{dash(app.jobTitle)}</DetailRow>
            <DetailRow label="Monthly income">{formatMoney(app.monthlyIncome)}</DetailRow>
            <FileGroup label="Proof of income" files={app.proofOfIncome} onOpen={open} />
          </DetailCard>

          <DetailCard icon="users" title={`References (${references.length})`}>
            {references.length ? (
              <div className="flex flex-col gap-2.5">
                {references.map((r, i) => (
                  <div key={i} className="rounded-xl border border-g100 bg-g50 px-4 py-3">
                    <div className="text-sm font-semibold text-ink">
                      {r.name || `Reference ${i + 1}`}
                      {r.relationship && <span className="text-g500 font-normal"> · {r.relationship}</span>}
                    </div>
                    <div className="text-[12.5px] text-g500 mt-0.5 flex flex-wrap gap-x-4 gap-y-0.5">
                      {r.phone && (
                        <a href={`tel:${r.phone}`} className="inline-flex items-center gap-1 hover:text-brand">
                          <Icon name="phone" size={12} /> {r.phone}
                        </a>
                      )}
                      {r.email && (
                        <a href={`mailto:${r.email}`} className="inline-flex items-center gap-1 hover:text-brand">
                          <Icon name="mail" size={12} /> {r.email}
                        </a>
                      )}
                      {!r.phone && !r.email && 'No contact details'}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <span className="text-[13px] text-g400">No references provided.</span>
            )}
          </DetailCard>

          <DetailCard icon="home" title="Previous Landlord">
            <DetailRow label="Name">{dash(app.previousLandlordName)}</DetailRow>
            <DetailRow label="Tenancy date">{fmtDate(app.tenancyDates)}</DetailRow>
          </DetailCard>
        </div>

        {/* Right: property, documents, payment */}
        <div className="flex flex-col gap-5">
          <DetailCard icon="building-2" title="Property">
            {listingPhotos.length > 0 && (
              <div className="flex gap-2.5 overflow-x-auto pb-1 mb-2">
                {listingPhotos.map((p, i) => (
                  <ImageThumb key={i} src={p} alt={`Property photo ${i + 1}`} size={96} onOpen={() => open(p, listTitle)} />
                ))}
              </div>
            )}
            <DetailRow label="Listing">
              <button
                type="button"
                className="text-brand hover:underline font-semibold text-right"
                onClick={() => navigate(`/admin/listings/${app.listingId}`)}
              >
                {listTitle}
              </button>
            </DetailRow>
            <DetailRow label="Address">{dash(listAddress)}</DetailRow>
            <DetailRow label="Type">{listType === 'Sale' ? 'Buy' : dash(listType)}</DetailRow>
            <DetailRow label={listType === 'Sale' ? 'Price' : 'Rent'}>
              {formatMoney(listPrice)}
              {listType === 'Rental' && listPrice !== null ? '/mo' : ''}
            </DetailRow>
            <DetailRow label="Beds / Baths">
              {beds === null && baths === null ? dash(null) : `${beds ?? '—'} bd · ${baths ?? '—'} ba`}
            </DetailRow>
            <DetailRow label="Listed by">
              {app.agent ? (
                agentIsMe ? (
                  `${app.agent.name || app.agent.email} (you)`
                ) : (
                  <button
                    type="button"
                    className="text-brand hover:underline font-semibold text-right"
                    onClick={() => navigate(`/admin/agents/${app.agent!.id}`)}
                  >
                    {app.agent.name || app.agent.email}
                  </button>
                )
              ) : (
                dash(null)
              )}
            </DetailRow>
          </DetailCard>

          <DetailCard icon="file-text" title="Documents">
            <FileGroup label="Government-issued ID" files={app.governmentId} onOpen={open} />
            <FileGroup label="Social security card" files={app.socialSecurityCard} onOpen={open} />
            <FileGroup label="Other documents" files={app.documents} onOpen={open} />
          </DetailCard>

          <DetailCard icon="credit-card" title="Payment">
            <DetailRow label="Application fee">{formatMoney(app.applicationFee)}</DetailRow>
            <DetailRow label="Payment method">{cap(app.paymentMethod)}</DetailRow>
            <DetailRow label="Payment status">
              {paid ? (
                <span className="inline-flex items-center gap-1" style={{ color: 'var(--green)' }}>
                  <Icon name="check-circle" size={14} color="var(--green)" /> Paid
                </span>
              ) : (
                <span className="text-g500">Unpaid</span>
              )}
            </DetailRow>
            <DetailRow label="Paid on">{fmtDateTime(app.paidAt)}</DetailRow>
            <DetailRow label="Certified accurate">{app.certified ? 'Yes' : 'No'}</DetailRow>
          </DetailCard>
        </div>
      </div>

      <ImageLightbox
        src={viewing ? viewing.src : null}
        onClose={() => setViewing(null)}
        title={viewing ? `${viewing.title} · ${app.referenceNo || '#' + app.id}` : 'Photo'}
      />
    </div>
  );
};

export default ApplicationDetail;
